import styled from 'styled-components'
import { tokens } from 'styling/tokens'

export const AnalyticsPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`

export const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

export const DateDropdown = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${tokens.accent.base};
  border-radius: ${tokens.radius.control};
  background: transparent;
  color: ${tokens.text.primary};
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;

  svg {
    fill: ${tokens.text.primary};
  }
`

export const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${tokens.text.secondary};
  border-radius: ${tokens.radius.control};
  background: transparent;
  color: ${tokens.text.primary};
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;

  svg {
    fill: ${tokens.text.primary};
  }
`

export const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${tokens.accent.base};
  border-radius: ${tokens.radius.control};
  background: transparent;
  color: ${tokens.accent.base};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  svg {
    fill: ${tokens.accent.base};
  }
`

export const BannerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 1rem;
  border: 1px solid ${tokens.status.success.fg};
  border-radius: ${tokens.radius.control};
  background: ${tokens.status.success.fg}15;
  width: 100%;
`

export const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${tokens.status.success.fg};
  font-size: 0.8rem;
  font-weight: 500;
`

export const BannerRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

export const BannerChip = styled.span`
  font-size: 0.75rem;
  color: ${tokens.text.secondary};
`

export const BannerClose = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${tokens.text.secondary};
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  padding: 0;
`

export const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`

export const KpiRow = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
`

export const KpiRowFill = styled(KpiRow)`
  & > * {
    flex: 1 1 0;
    min-width: 0;
  }
`

export const ChartCardWrapper = styled.div`
  background: ${tokens.surface.glass};
  backdrop-filter: blur(${tokens.blur});
  border: 1px solid ${tokens.border.hairline};
  box-shadow: ${tokens.shadow.card};
  border-radius: ${tokens.radius.card};
  padding: 1.25rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const ChartCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

export const ChartCardTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${tokens.text.primary};
`

export const ChartCardControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

export const PeriodToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border-radius: 0.8rem;
  overflow: hidden;
  background: ${tokens.surface.raised};
`

export const PeriodButton = styled.button<{ active?: boolean }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.8rem;
  font-size: 0.75rem;
  font-weight: ${(props) => (props.active ? '700' : '400')};
  color: ${(props) => (props.active ? tokens.accent.ink : tokens.text.secondary)};
  background: ${(props) => (props.active ? tokens.accent.base : 'transparent')};
  cursor: pointer;
  transition: all 0.2s;
`

export const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.7rem;
  color: ${tokens.text.secondary};
`

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`

export const LegendDot = styled.div<{ color: string; dashed?: boolean }>`
  width: ${(props) => (props.dashed ? '1.25rem' : '0.5rem')};
  height: ${(props) => (props.dashed ? '2px' : '0.5rem')};
  border-radius: ${(props) => (props.dashed ? '0' : '50%')};
  background: ${(props) => (props.dashed ? 'transparent' : props.color)};
  ${(props) => props.dashed && `border-top: 2px dashed ${props.color};`}
`

export const AnalyticsTableWrapper = styled.div`
  width: 100%;
  padding: 0 1rem;
  ${(props) => props.theme.content};
  border-radius: 10px;
  overflow: hidden;
`

export const TableHeaderCell = styled.div<{ sortable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  cursor: ${(props) => (props.sortable ? 'pointer' : 'default')};
  user-select: none;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  color: ${tokens.text.secondary};
`

export const SortIcon = styled.span<{ direction?: 'asc' | 'desc' | null }>`
  display: inline-flex;
  font-size: 0.6rem;
  color: ${(props) => (props.direction ? tokens.accent.base : tokens.text.secondary)};
`

export const RateCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`

export const RateBar = styled.div<{ percent: number; color: string }>`
  position: relative;
  height: 4px;
  border-radius: 2px;
  background: ${tokens.border.hairline};
  flex: 1;
  max-width: 80%;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    min-width: 6px;
    width: ${(props) => Math.max(0, Math.min(100, props.percent))}%;
    background: ${(props) => props.color};
    border-radius: 2px;
  }
`

export const RateValue = styled.span<{ color: string }>`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(props) => props.color};
  white-space: nowrap;
`

export const LockedCard = styled.div`
  ${(props) => props.theme.content};
  border-radius: 10px;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  min-height: 12rem;
`

export const LockedIcon = styled.div`
  color: ${tokens.text.secondary};
  font-size: 2rem;

  svg {
    width: 2.5rem;
    height: 2.5rem;
    fill: ${tokens.text.secondary};
  }
`

export const InfoBanner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  font-size: 0.8rem;
  color: ${tokens.text.secondary};
  width: 100%;
`

export const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
`

export const OverviewCard = styled.div`
  ${(props) => props.theme.content};
  border-radius: 10px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    outline: 1px solid ${tokens.accent.base};
  }
`

export const OverviewCardRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
`

export const OverviewCardLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`

export const OverviewCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

export const OverviewCardIcon = styled.div<{ color?: string }>`
  color: ${(props) => props.color || tokens.accent.base};

  svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: ${(props) => props.color || tokens.accent.base};
  }
`

export const OverviewCardValue = styled.div<{ color?: string }>`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${(props) => props.color || tokens.status.success.fg};
  white-space: nowrap;
  flex-shrink: 0;
  padding-top: 0.125rem;
`

export const HorizontalBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`

export const HorizontalBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`

export const HorizontalBarLabel = styled.div`
  font-size: 0.8rem;
  color: ${tokens.text.primary};
  min-width: 7rem;
  text-align: right;
`

export const HorizontalBarTrack = styled.div`
  flex: 1;
  height: 1.25rem;
  background: ${tokens.surface.inset};
  border-radius: 4px;
  overflow: hidden;
`

export const HorizontalBarFill = styled.div<{ percent: number; color?: string }>`
  height: 100%;
  width: ${(props) => props.percent}%;
  background: ${(props) => props.color || tokens.accent.base};
  border-radius: 4px;
  transition: width 0.3s ease;
`

export const HorizontalBarValue = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${tokens.text.primary};
  min-width: 3.5rem;
  text-align: right;
`

export const StatusBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`

export const StatusBar = styled.div`
  display: flex;
  width: 100%;
  height: 2rem;
  border-radius: 6px;
  overflow: hidden;
`

export const StatusSegment = styled.div<{ percent: number; color: string }>`
  width: ${(props) => props.percent}%;
  background: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: ${tokens.text.primary};
  min-width: ${(props) => (props.percent > 5 ? 'auto' : '0')};
  overflow: hidden;
`

export const StatusLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.7rem;
  color: ${tokens.text.secondary};
`

